/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import {
  ArrowRightOutlined,
  BankOutlined,
  CalendarOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { CompetitionResult } from '@sportninja/common/features/teamFinder/types'
import { formatScheduleDateRange } from '@sportninja/common/features/teamFinder/scheduleDates'
import { buildRegistrationUrl } from '@sportninja/common/features/registration/registrationUrlParams'
import {
  font,
  weightSemiBold,
  weightBold,
  weightMedium,
  paragraphSmall,
  paragraphMedium,
  paragraphXSmall,
} from 'src/components/css'
import { useResultLabels } from '../hooks/useResultLabels'
import { useResultSport } from '../hooks/useResultSport'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface Props {
  result: CompetitionResult
}

const CompetitionResultCard = ({ result }: Props) => {
  const { genderLabel, positionNames } = useResultLabels(result)

  const { sportName, fallbackBanner } = useResultSport(result)

  const teamLogo = resolveAssetUrl(result.teamImage) ?? TEAM_PLACEHOLDER
  // No competition banner of its own — fall back to the sport's artwork, the
  // same image the competition page header uses.
  const banner = resolveAssetUrl(result.scheduleImage) ?? fallbackBanner

  // Only show the competition line when it differs from the team name (they're
  // often identical in the data, which reads as duplication).
  const showCompetition =
    result.competitionName &&
    result.competitionName.toLowerCase() !== result.teamName.toLowerCase()

  // Top chips: sport, gender category, youth/adult, division age.
  const chips: string[] = []
  if (sportName) {
    chips.push(capitalizeWords(sportName))
  }
  if (genderLabel) {
    chips.push(genderLabel)
  }
  if (result.isYouth != null) {
    chips.push(result.isYouth ? 'Youth' : 'Adult')
  }
  if (result.divisionAge) {
    chips.push(result.divisionAge)
  }

  // Competition run dates — shown regardless of whether the competition name
  // row is hidden as a duplicate of the team name.
  const scheduleDates = formatScheduleDateRange(
    result.scheduleStartsAt,
    result.scheduleEndsAt
  )

  const availableDays = result.availableDays ?? []
  const availableTimes = result.availableTimes ?? []

  const body = (
    <>
      {/* Banner with the team logo avatar overlapping its bottom edge. */}
      <div css={bannerStyle}>
        <img css={bannerImageStyle} src={banner} alt='' />
        {result.spotsRemaining != null ? (
          <span css={spotsBadgeStyle}>
            {result.spotsRemaining} spot
            {result.spotsRemaining === 1 ? '' : 's'} left
          </span>
        ) : null}
        <img css={avatarStyle} src={teamLogo} alt={result.teamName} />
      </div>

      <div css={bodyStyle}>
        {result.teamId ? (
          <a
            css={[teamNameStyle, linkTextStyle]}
            href={`/team/${result.teamId}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            {result.teamName}
          </a>
        ) : (
          <span css={teamNameStyle}>{result.teamName}</span>
        )}

        {showCompetition ? (
          result.scheduleId ? (
            <a
              css={[competitionStyle, linkTextStyle]}
              href={`/schedule/${result.scheduleId}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {result.competitionName}
            </a>
          ) : (
            <span css={competitionStyle}>{result.competitionName}</span>
          )
        ) : null}

        {/* Organization line: the competition's parent org, directly under the
            competition it belongs to. */}
        {result.organizationName ? (
          <span css={organizationStyle}>
            <BankOutlined />
            {result.organizationId ? (
              <a
                css={linkTextStyle}
                href={`/org/${result.organizationId}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                {result.organizationName}
              </a>
            ) : (
              result.organizationName
            )}
          </span>
        ) : null}

        {scheduleDates ? (
          <span css={scheduleDatesStyle}>
            <CalendarOutlined /> {scheduleDates}
          </span>
        ) : null}

        {chips.length ? (
          <div css={chipRowStyle}>
            {chips.map((c) => (
              <span key={c} css={chipStyle}>
                {c}
              </span>
            ))}
          </div>
        ) : null}

        {availableDays.length ? (
          <div css={nightsBlockStyle}>
            <span css={metaLabelStyle}>
              {capitalizeWords('Available Days')}
            </span>
            <div css={chipRowStyle}>
              {availableDays.map((d) => (
                <span key={d} css={dayChipStyle}>
                  {d}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {availableTimes.length ? (
          <div css={nightsBlockStyle}>
            <span css={metaLabelStyle}>
              {capitalizeWords('Available Times')}
            </span>
            <div css={chipRowStyle}>
              {availableTimes.map((t) => (
                <span key={t} css={dayChipStyle}>
                  {t}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {positionNames.length ? (
          <div css={nightsBlockStyle}>
            <span css={metaLabelStyle}>
              {capitalizeWords('Positions Needed')}
            </span>
            <span css={positionsStyle}>{positionNames.join(', ')}</span>
          </div>
        ) : null}

        {result.notes ? <span css={notesStyle}>“{result.notes}”</span> : null}

        {result.scheduleId ? (
          <a
            css={ctaStyle}
            href={buildRegistrationUrl(result.scheduleId, {
              teamId: result.teamId,
            })}
            target='_blank'
            rel='noopener noreferrer'
          >
            {capitalizeWords('View & Register')} <ArrowRightOutlined />
          </a>
        ) : null}
      </div>
    </>
  )

  // The card is a plain container, not a link: the team name, competition name
  // and CTA each open their own destination in a new tab, and an anchor can't
  // legally nest inside another anchor.
  return <div css={cardStyle}>{body}</div>
}

export default CompetitionResultCard

// ─── Styles ──────────────────────────────────────────────────────────────────

const cardStyle = css`
  display: flex;
  flex-direction: column;
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 16px;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${colors.PRIMARY};
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
  }
`
// Inline links (team name, competition name): inherit the surrounding type,
// underline only on hover so the card doesn't read as a wall of links.
const linkTextStyle = css`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    color: ${colors.PRIMARY};
    text-decoration: underline;
  }
`
const bannerStyle = css`
  position: relative;
  height: 108px;
`
const bannerImageStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`
const spotsBadgeStyle = css`
  position: absolute;
  top: 12px;
  right: 12px;
  ${paragraphXSmall};
  ${weightBold};
  /* Content on a PRIMARY fill comes from the shared config — it knows the
     right colour per tenant and platform (dark on SportNinja and Canlan web). */
  color: ${PRIMARY_BUTTON_CONTENT.text};
  background-color: ${colors.PRIMARY};
  padding: 4px 10px;
  border-radius: 999px;
`
// Circular team logo overlapping the banner's bottom-left edge.
const avatarStyle = css`
  position: absolute;
  left: 16px;
  bottom: -28px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: contain;
  background-color: ${colors.NEUTRAL_0};
  border: 3px solid ${colors.SECONDARY_400};
`
const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 40px 16px 16px;
`
const teamNameStyle = css`
  ${font.body};
  ${weightBold};
  ${paragraphMedium};
  color: ${colors.WHITE};
`
const competitionStyle = css`
  ${paragraphSmall};
  color: ${colors.NEUTRAL_50};
`
/* Organization line: same muted treatment as the date row, so the team name
   stays the card's only prominent text. */
const organizationStyle = css`
  ${paragraphXSmall};
  ${weightMedium};
  color: ${colors.NEUTRAL_50};
  display: inline-flex;
  align-items: center;
  gap: 6px;
`
const scheduleDatesStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_50};
  display: inline-flex;
  align-items: center;
  gap: 6px;
`
const chipRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`
const chipStyle = css`
  ${paragraphXSmall};
  ${weightMedium};
  color: ${colors.NEUTRAL_0};
  background-color: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_200};
  padding: 2px 10px;
  border-radius: 999px;
`
const dayChipStyle = css`
  ${paragraphXSmall};
  ${weightMedium};
  color: ${colors.PRIMARY};
  background-color: transparent;
  border: 1px solid ${colors.PRIMARY};
  padding: 2px 10px;
  border-radius: 999px;
`
const nightsBlockStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`
const metaLabelStyle = css`
  ${paragraphXSmall};
  ${weightSemiBold};
  color: ${colors.NEUTRAL_100};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`
const positionsStyle = css`
  ${paragraphSmall};
  color: ${colors.NEUTRAL_0};
`
const notesStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_50};
  font-style: italic;
  margin-top: 4px;
`
const ctaStyle = css`
  ${paragraphXSmall};
  ${weightSemiBold};
  color: ${colors.PRIMARY};
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  margin-top: 8px;
  text-decoration: none;

  &:hover {
    color: ${colors.PRIMARY};
    text-decoration: underline;
  }
`
